<template>
  <div class="live-publish-plan">
    <!-- 顶部操作栏 -->
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新建计划组</el-button>
      <el-button icon="el-icon-refresh" @click="loadList">刷新</el-button>
    </div>

    <!-- 计划列表 -->
    <el-table
      :data="pagedList"
      border
      stripe
      v-loading="loading"
      style="width: 100%"
      :empty-text="'暂无数据'"
    >
      <el-table-column prop="name" label="计划名称" min-width="160" show-overflow-tooltip />
      <el-table-column prop="flowCode" label="发布流程" width="180" show-overflow-tooltip />
      <el-table-column label="模式" width="70">
        <template slot-scope="scope">
          <el-tag size="small" :type="modeTagType(scope.row.mode)">
            {{ modeLabel(scope.row.mode) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="启用" width="70" align="center">
        <template slot-scope="scope">
          <el-switch
            v-model="scope.row.enabled"
            @change="toggleItemEnabled(scope.row)"
            active-color="#13ce66"
            inactive-color="#ff4949"
          />
        </template>
      </el-table-column>
      <el-table-column label="关联账号" min-width="140">
        <template slot-scope="scope">
          <el-tag
            v-for="(name, i) in (scope.row.accountNames || scope.row.accountIds || [])"
            :key="i"
            size="small"
            style="margin: 2px"
          >{{ name || (scope.row.accountIds && scope.row.accountIds[i]) || '' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="执行次数" width="90" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.runtime">{{ scope.row.runtime.runCount || 0 }}</span>
          <span v-else>0</span>
        </template>
      </el-table-column>
      <el-table-column label="下次执行" width="160" show-overflow-tooltip>
        <template slot-scope="scope">
          <span v-if="scope.row.runtime && scope.row.runtime.nextRunAt">
            {{ scope.row.runtime.nextRunAt }}
          </span>
          <span v-else style="color: #909399">—</span>
        </template>
      </el-table-column>
      <el-table-column label="最近状态" width="80" align="center">
        <template slot-scope="scope">
          <el-tag
            v-if="scope.row.runtime && scope.row.runtime.lastStatus"
            size="small"
            :type="statusTagType(scope.row.runtime.lastStatus)"
          >{{ scope.row.runtime.lastStatus }}</el-tag>
          <span v-else style="color: #909399">—</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="240" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="primary" plain @click="openEditDialog(scope.row)">编辑</el-button>
          <el-button size="mini" type="success" plain @click="triggerGroup(scope.row)">触发</el-button>
          <el-button size="mini" @click="viewRuns(scope.row)">记录</el-button>
          <el-button size="mini" type="danger" plain @click="deleteGroup(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-if="total > pageSize"
      @current-change="handlePageChange"
      :current-page="currentPage"
      :page-size="pageSize"
      layout="total, prev, pager, next"
      :total="total"
      style="margin-top: 16px; text-align: right"
    />

    <!-- 新建/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="650px"
      :close-on-click-modal="false"
      @close="resetForm"
    >
      <el-form
        ref="planForm"
        :model="form"
        :rules="formRules"
        label-width="100px"
        label-position="right"
      >
        <el-form-item label="计划名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入计划名称" maxlength="128" />
        </el-form-item>

        <el-form-item label="发布流程" prop="flowCode">
          <el-select
            v-model="form.flowCode"
            placeholder="请选择发布流程"
            filterable
            style="width: 100%"
            @focus="loadFlowList"
          >
            <el-option
              v-for="flow in flowList"
              :key="flow.flowCode"
              :label="flow.flowName + ' (' + flow.flowCode + ')'"
              :value="flow.flowCode"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="关联账号" prop="accountIds">
          <el-select
            v-model="form.accountIds"
            placeholder="请选择发布账号（可多选）"
            multiple
            filterable
            style="width: 100%"
            @focus="loadAccountList"
          >
            <el-option
              v-for="acc in accountList"
              :key="acc.id"
              :label="acc.name ? acc.name + ' (ID:' + acc.id + ')' : 'ID: ' + acc.id"
              :value="String(acc.id)"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="发布模式" prop="mode">
          <el-radio-group v-model="form.mode">
            <el-radio label="once">单次执行</el-radio>
            <el-radio label="schedule">定时执行</el-radio>
            <el-radio label="loop">循环执行</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 定时规则 (mode=schedule) -->
        <template v-if="form.mode === 'schedule'">
          <el-form-item label="定时类型" prop="scheduleType">
            <el-select v-model="form.scheduleType" placeholder="请选择" style="width: 100%">
              <el-option label="每天" value="daily" />
              <el-option label="每周" value="weekly" />
              <el-option label="自定义Cron" value="cron" />
            </el-select>
          </el-form-item>
          <el-form-item v-if="form.scheduleType === 'daily'" label="执行时间">
            <el-time-picker v-model="form.scheduleTime" format="HH:mm" value-format="HH:mm" placeholder="选择时间" style="width: 100%"/>
          </el-form-item>
          <el-form-item v-if="form.scheduleType === 'weekly'" label="星期">
            <el-select v-model="form.scheduleWeekdays" multiple placeholder="选择星期" style="width: 100%">
              <el-option label="周一" :value="1" /><el-option label="周二" :value="2" />
              <el-option label="周三" :value="3" /><el-option label="周四" :value="4" />
              <el-option label="周五" :value="5" /><el-option label="周六" :value="6" />
              <el-option label="周日" :value="7" />
            </el-select>
          </el-form-item>
          <el-form-item v-if="form.scheduleType === 'cron'" label="Cron表达式">
            <el-input v-model="form.scheduleCron" placeholder="如: 0 30 9 * * ?" />
          </el-form-item>
        </template>

        <!-- 循环规则 (mode=loop) -->
        <template v-if="form.mode === 'loop'">
          <el-form-item label="间隔(分钟)" prop="loopInterval">
            <el-input-number v-model="form.loopInterval" :min="1" :max="1440" style="width: 100%"/>
          </el-form-item>
          <el-form-item label="立即执行">
            <el-switch v-model="form.loopRunImmediately" active-text="是" inactive-text="否" />
          </el-form-item>
        </template>

        <!-- 限制规则 -->
        <el-form-item label="最大执行次数">
          <el-input-number v-model="form.maxRuns" :min="0" placeholder="0=不限制" style="width: 100%"/>
        </el-form-item>

        <!-- 启用 -->
        <el-form-item label="启用状态">
          <el-switch v-model="form.enabled" active-text="启用" inactive-text="停用" />
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saveLoading" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>

    <!-- 触发确认 -->
    <el-dialog title="确认触发" :visible.sync="triggerVisible" width="400px">
      <p>确定要立即触发执行计划组 <b>{{ triggerTarget ? triggerTarget.name : '' }}</b> 吗？</p>
      <div slot="footer">
        <el-button @click="triggerVisible = false">取消</el-button>
        <el-button type="primary" :loading="triggerLoading" @click="confirmTrigger">确认触发</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from "@/utils/axios";
import message from "@/utils/message";

const API_BASE = window.location.protocol + "//" + window.location.host + "/trc20/qdd/api/livePublish";
const MGR_BASE = "/trc20/qdd/manager";

export default {
  name: "LivePublishPlan",
  data() {
    return {
      loading: false,
      saveLoading: false,
      triggerLoading: false,

      planList: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,

      dialogVisible: false,
      dialogTitle: "新建计划组",
      isEdit: false,
      editGroupId: null,

      form: {
        name: "", flowCode: "", accountIds: [], mode: "once", enabled: true,
        scheduleType: "daily", scheduleTime: null, scheduleWeekdays: [], scheduleCron: "",
        loopInterval: 30, loopRunImmediately: false, maxRuns: 0,
      },

      formRules: {
        name: [{ required: true, message: "请输入计划名称", trigger: "blur" }],
        flowCode: [{ required: true, message: "请选择发布流程", trigger: "change" }],
        accountIds: [{ type: "array", required: true, message: "请至少选择1个账号", trigger: "change" }],
        mode: [{ required: true, message: "请选择模式", trigger: "change" }],
      },

      flowList: [],
      accountList: [],

      triggerVisible: false,
      triggerTarget: null,
    };
  },

  computed: {
    pagedList() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.planList.slice(start, start + this.pageSize);
    },
  },

  mounted() {
    this.loadList();
  },

  methods: {
    loadList() {
      this.loading = true;
      request.post({
        url: API_BASE + "/plan/list",
        data: {},
        success: (res) => {
          this.loading = false;
          if (res.code === 0) {
            this.planList = res.data || [];
            this.total = this.planList.length;
            this.currentPage = 1;
          } else {
            message.error(res.msg || "加载失败");
          }
        },
        error: () => { this.loading = false; message.error("请求失败"); },
      });
    },

    loadFlowList() {
      if (this.flowList.length > 0) return;
      request.post({
        url: API_BASE + "/flow/list",
        data: {},
        success: (res) => { if (res.code === 0) this.flowList = res.data || []; },
        error: () => {},
      });
    },

    loadAccountList() {
      if (this.accountList.length > 0) return;
      request.post({
        url: MGR_BASE + "/aDSEnvironment/listByCondition",
        data: { page: 1, limit: 500 },
        success: (res) => { if (res.code === 0) this.accountList = res.data || []; },
        error: () => {},
      });
    },

    openCreateDialog() {
      this.dialogTitle = "新建计划组";
      this.isEdit = false;
      this.editGroupId = null;
      this.dialogVisible = true;
      this.$nextTick(() => { this.$refs.planForm && this.$refs.planForm.clearValidate(); });
    },

    openEditDialog(row) {
      this.dialogTitle = "编辑计划组";
      this.isEdit = true;
      this.editGroupId = row.groupId;
      this.form.name = row.name || "";
      this.form.flowCode = row.flowCode || "";
      this.form.accountIds = row.accountIds || [];
      this.form.mode = row.mode || "once";
      this.form.enabled = row.enabled !== false;
      this.form.maxRuns = (row.limitRule && row.limitRule.maxRuns) || 0;

      if (row.scheduleRule) {
        this.form.scheduleType = row.scheduleRule.type || "daily";
        this.form.scheduleTime = row.scheduleRule.time || null;
        this.form.scheduleWeekdays = row.scheduleRule.weekdays || [];
        this.form.scheduleCron = row.scheduleRule.cron || "";
      } else {
        this.form.scheduleType = "daily"; this.form.scheduleTime = null;
        this.form.scheduleWeekdays = []; this.form.scheduleCron = "";
      }
      if (row.loopRule) {
        this.form.loopInterval = row.loopRule.intervalMinutes || 30;
        this.form.loopRunImmediately = !!row.loopRule.runImmediately;
      } else {
        this.form.loopInterval = 30; this.form.loopRunImmediately = false;
      }

      this.dialogVisible = true;
      this.$nextTick(() => { this.$refs.planForm && this.$refs.planForm.clearValidate(); });
    },

    submitForm() {
      this.$refs.planForm.validate((valid) => {
        if (!valid) return;
        const payload = {
          name: this.form.name, flowCode: this.form.flowCode,
          accountIds: this.form.accountIds, mode: this.form.mode,
          enabled: this.form.enabled,
        };
        if (this.form.mode === "schedule") {
          payload.scheduleRule = { type: this.form.scheduleType };
          if (this.form.scheduleType === "daily" && this.form.scheduleTime) payload.scheduleRule.time = this.form.scheduleTime;
          if (this.form.scheduleType === "weekly" && this.form.scheduleWeekdays.length > 0) payload.scheduleRule.weekdays = this.form.scheduleWeekdays;
          if (this.form.scheduleType === "cron" && this.form.scheduleCron) payload.scheduleRule.cron = this.form.scheduleCron;
        }
        if (this.form.mode === "loop") {
          payload.loopRule = { intervalMinutes: this.form.loopInterval, runImmediately: this.form.loopRunImmediately };
        }
        if (this.form.maxRuns > 0) payload.limitRule = { maxRuns: this.form.maxRuns };
        if (this.isEdit) payload.groupId = this.editGroupId;

        this.saveLoading = true;
        request.post({
          url: API_BASE + "/plan/saveGroup", data: payload,
          success: (res) => {
            this.saveLoading = false;
            if (res.code === 0) {
              message.success(this.isEdit ? "修改成功" : "创建成功");
              this.dialogVisible = false; this.loadList();
            } else { message.error(res.msg || "保存失败"); }
          },
          error: () => { this.saveLoading = false; message.error("请求失败"); },
        });
      });
    },

    toggleItemEnabled(row) {
      request.post({
        url: API_BASE + "/plan/toggle",
        data: { planId: row.planId, enabled: row.enabled },
        success: (res) => {
          if (res.code !== 0) { row.enabled = !row.enabled; message.error(res.msg || "操作失败"); }
          else message.success(row.enabled ? "已启用" : "已停用");
        },
        error: () => { row.enabled = !row.enabled; message.error("请求失败"); },
      });
    },

    triggerGroup(row) {
      this.triggerTarget = row;
      this.triggerVisible = true;
    },

    confirmTrigger() {
      this.triggerLoading = true;
      request.post({
        url: API_BASE + "/plan/triggerGroup",
        data: { groupId: this.triggerTarget.groupId, source: "desktop_plan_ui" },
        success: (res) => {
          this.triggerLoading = false; this.triggerVisible = false;
          if (res.code === 0) { message.success("触发成功，排队 " + (res.data ? res.data.queued || 0 : 0) + " 个"); this.loadList(); }
          else message.error(res.msg || "触发失败");
        },
        error: () => { this.triggerLoading = false; this.triggerVisible = false; message.error("请求失败"); },
      });
    },

    deleteGroup(row) {
      this.$confirm("确定删除计划组「" + row.name + "」？不可恢复。", "确认删除", { type: "warning" })
        .then(() => {
          request.post({
            url: API_BASE + "/plan/deleteGroup", data: { groupId: row.groupId },
            success: (res) => { if (res.code === 0) { message.success("已删除"); this.loadList(); } else message.error(res.msg || "删除失败"); },
            error: () => message.error("请求失败"),
          });
        })
        .catch(() => {});
    },

    viewRuns(row) {
      this.$router.push({
        path: this.feUrl + "/livePublishPlanRun.html",
        query: { groupId: row.groupId, groupName: row.name },
      });
    },

    resetForm() {
      if (this.$refs.planForm) this.$refs.planForm.resetFields();
      Object.assign(this.form, {
        name: "", flowCode: "", accountIds: [], mode: "once", enabled: true,
        scheduleType: "daily", scheduleTime: null, scheduleWeekdays: [], scheduleCron: "",
        loopInterval: 30, loopRunImmediately: false, maxRuns: 0,
      });
    },

    handlePageChange(page) { this.currentPage = page; },

    modeLabel(m) { return { once: "单次", schedule: "定时", loop: "循环" }[m] || m; },
    modeTagType(m) { return { once: "info", schedule: "warning", loop: "success" }[m] || "info"; },
    statusTagType(s) { return { success: "success", failed: "danger", skipped: "warning", running: "primary", dispatching: "primary" }[s] || "info"; },
  },
};
</script>

<style scoped>
.live-publish-plan { padding: 16px; }
.toolbar { margin-bottom: 16px; display: flex; gap: 8px; }
</style>
